/**
 * Quote Workflow Service
 *
 * Handles business logic and workflows for quotes.
 * This service manages state transitions, quote number generation, and other business operations.
 */

import { RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Quote } from '@/shared/types';

/**
 * Quote Workflow Service
 * Provides business logic and workflow operations for quotes
 */
export class QuoteWorkflowService {
  private tableName = 'quotes' as const;

  /**
   * Generate a unique quote number for a user
   */
  async generateQuoteNumber(userId: string): Promise<string> {
    try {
      const db = getDbClient();

      const { data } = await db
        .from(this.tableName)
        .select('quote_number')
        .eq('user_id', userId)
        .order('quote_number', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!data) {
        return 'Q-001';
      }

      // Extract number from quote number (e.g., "Q-001" -> 1)
      const lastNum = parseInt(data.quote_number.split('-')[1] || '0');
      return `Q-${String(lastNum + 1).padStart(3, '0')}`;
    } catch {
      return 'Q-001';
    }
  }

  /**
   * Generate a public token for client portal access
   */
  generatePublicToken(): string {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Validate quote data before creation/update
   */
  validateQuoteData(quoteData: Partial<Quote>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!quoteData.title || quoteData.title.trim() === '') {
      errors.push('Quote title is required');
    }

    if (!quoteData.clientId) {
      errors.push('Client is required');
    }

    if (!quoteData.validUntil) {
      errors.push('Valid until date is required');
    } else {
      const validUntil = new Date(quoteData.validUntil);
      if (isNaN(validUntil.getTime())) {
        errors.push('Valid until date must be a valid date');
      } else {
        // Check if valid until is in the future
        const now = new Date();
        if (validUntil <= now) {
          errors.push('Valid until date must be in the future');
        }
      }
    }

    if (quoteData.totalAmount !== undefined && quoteData.totalAmount < 0) {
      errors.push('Total amount cannot be negative');
    }

    if (quoteData.subtotal !== undefined && quoteData.subtotal < 0) {
      errors.push('Subtotal cannot be negative');
    }

    if (quoteData.taxAmount !== undefined && quoteData.taxAmount < 0) {
      errors.push('Tax amount cannot be negative');
    }

    if (quoteData.taxRate !== undefined && (quoteData.taxRate < 0 || quoteData.taxRate > 100)) {
      errors.push('Tax rate must be between 0 and 100');
    }

    if (!quoteData.items || quoteData.items.length === 0) {
      errors.push('At least one item is required');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Calculate quote totals from items
   */
  calculateTotals(
    items: Array<{ rate: number; quantity: number }>,
    taxRate: number
  ): { subtotal: number; taxAmount: number; totalAmount: number } {
    const subtotal = items.reduce((sum, item) => sum + item.rate * item.quantity, 0);
    const taxAmount = subtotal * (taxRate / 100);
    const totalAmount = subtotal + taxAmount;

    return { subtotal, taxAmount, totalAmount };
  }

  /**
   * Duplicate a quote (create a new quote from existing one)
   */
  async duplicateQuote(quoteId: string): Promise<RepositoryResult<Quote>> {
    try {
      // Import dynamically to avoid circular dependency
      const { quoteQueryRepository } = await import('./QuoteQueryRepository');

      // Get the original quote
      const result = await quoteQueryRepository.findById(quoteId);

      if (!result.success || !result.data) {
        return {
          success: false,
          error: 'Quote not found',
        };
      }

      const original = result.data;

      // Get user ID from the quote
      const db = getDbClient();
      const { data } = await db
        .from(this.tableName)
        .select('user_id')
        .eq('id', quoteId)
        .single();

      if (!data) {
        return {
          success: false,
          error: 'Unable to determine quote owner',
        };
      }

      // Generate new quote number and token
      const newQuoteNumber = await this.generateQuoteNumber(data.user_id);
      const newPublicToken = this.generatePublicToken();

      // Create duplicate quote
      const duplicateData: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'> = {
        userId: data.user_id,
        clientId: original.clientId,
        title: `${original.title} (Copy)`,
        description: original.description,
        status: 'draft',
        currency: original.currency,
        subtotal: original.subtotal,
        taxRate: original.taxRate,
        taxAmount: original.taxAmount,
        totalAmount: original.totalAmount,
        validUntil: original.validUntil,
        items: original.items || [],
        notes: original.notes,
        terms: original.terms,
        quoteNumber: newQuoteNumber,
        publicToken: newPublicToken,
      };

      // Import command repository
      const { quoteCommandRepository } = await import('./QuoteCommandRepository');

      return quoteCommandRepository.create(data.user_id, duplicateData, newQuoteNumber, newPublicToken);
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'duplicating quote'),
      };
    }
  }

  /**
   * Send quote (change status to sent)
   */
  async sendQuote(quoteId: string): Promise<RepositoryResult<Quote>> {
    try {
      // Import command repository
      const { quoteCommandRepository } = await import('./QuoteCommandRepository');

      return quoteCommandRepository.updateStatus(quoteId, 'sent');
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'sending quote'),
      };
    }
  }

  /**
   * Accept quote
   */
  async acceptQuote(quoteId: string): Promise<RepositoryResult<Quote>> {
    try {
      // Import command repository
      const { quoteCommandRepository } = await import('./QuoteCommandRepository');

      return quoteCommandRepository.updateStatus(quoteId, 'accepted');
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'accepting quote'),
      };
    }
  }

  /**
   * Reject quote
   */
  async rejectQuote(quoteId: string): Promise<RepositoryResult<Quote>> {
    try {
      // Import command repository
      const { quoteCommandRepository } = await import('./QuoteCommandRepository');

      return quoteCommandRepository.updateStatus(quoteId, 'rejected');
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'rejecting quote'),
      };
    }
  }

  /**
   * Check if quote is eligible for status change
   */
  canChangeStatus(currentStatus: Quote['status'], newStatus: Quote['status']): boolean {
    const validTransitions: Record<string, string[]> = {
      'draft': ['sent', 'rejected'],
      'sent': ['draft', 'accepted', 'rejected'],
      'accepted': ['sent'], // Can revert
      'rejected': ['draft'], // Can reactivate
    };

    return validTransitions[currentStatus]?.includes(newStatus) || false;
  }

  /**
   * Helper method to handle errors consistently
   */
  private handleError(error: any, context: string): string {
    console.error(`Error ${context}:`, error);
    return error?.message || `Failed to ${context}`;
  }
}

// Export singleton instance
export const quoteWorkflowService = new QuoteWorkflowService();
